"use client";

import { useState } from "react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { Handoff } from "@/lib/onboarding/discovery";

import { TYPE_LABEL_MSGS, TYPE_LABELS } from "@/lib/agent-type-labels";
import { HireNameField, nameSubmitBlocked } from "@/components/onboarding/hire-name-field";

// Re-exported for existing client-side consumers (onboarding-flow). Server
// components must import from @/lib/agent-type-labels directly — importing
// through this "use client" module hands them a client-reference proxy.
export { TYPE_LABELS };
// What each type is good for — static platform knowledge from the agent-type
// catalog, shown so the user understands the roles (not agent-generated).
// Lingui descriptors — resolve with `i18n._(...)` at render.
export const TYPE_BLURBS: Record<string, MessageDescriptor> = {
  pa: msg`Tracks your commitments and follow-ups, and nudges you before things slip.`,
  pm: msg`Tracks project status, decisions, and blockers, and builds shareable status views.`,
  qa: msg`Tests your product, clicks through flows, and files issues when something breaks.`,
  ops: msg`Runs recurring operational workflows so routine work doesn't pile up.`,
};
export const TYPE_ORDER = ["pa", "pm", "qa", "ops"] as const;

interface HandoffCardProps {
  /** The bot's display name — pre-filled, editable here at the hand-off. */
  botName: string;
  /** Called as the user edits the name. */
  onNameChange: (name: string) => void;
  /** Availability message for the current name (null = available). */
  nameError: string | null;
  /** True while the name availability check is in flight. */
  checkingName: boolean;
  /** The discovery agent's handoff payload. */
  suggestion: Handoff;
  /** The agent type the user has settled on (may override the suggestion). */
  chosenType: string;
  /** Called when the user picks a different agent type. */
  onChooseType: (slug: string) => void;
  /** Called when the user confirms and starts the hire. */
  onConfirm: () => void;
  /** True while the hire request is in flight. */
  hiring: boolean;
}

/**
 * The handoff ("hand over") card shown at the end of discovery, just before the
 * agent is hired. The suggested type is stated inline as a near-settled choice
 * with a one-line reason; the full picker is tucked behind "See other roles" so
 * the user can override without being quizzed up front.
 *
 * i18n: English chrome stays as labels only. The agent-generated reason
 * (`type_reason`) is rendered as its own standalone line in the conversation's
 * language — never spliced into an English sentence. The static type blurbs are
 * English and only ever sit on their own line / in the picker.
 */
export function HandoffCard({ botName, onNameChange, nameError, checkingName, suggestion, chosenType, onChooseType, onConfirm, hiring }: HandoffCardProps) {
  const { i18n } = useLingui();
  const [showRoles, setShowRoles] = useState(false);
  const isSuggested = chosenType === suggestion.agent_type;
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated shadow-sm p-5 space-y-4">
      <div className="space-y-3">
        {suggestion.problem && (
          <div>
            <div className="text-xs text-foreground-muted mb-0.5"><Trans>What I&apos;ll help with</Trans></div>
            <div className="text-sm text-foreground">{suggestion.problem}</div>
          </div>
        )}
        {suggestion.proposed_first_action && (
          <div>
            <div className="text-xs text-foreground-muted mb-0.5"><Trans>First step</Trans></div>
            <div className="text-sm text-foreground">{suggestion.proposed_first_action}</div>
          </div>
        )}
      </div>

      <HireNameField
        id="hire-name"
        botName={botName}
        onNameChange={onNameChange}
        nameError={nameError}
        checkingName={checkingName}
      />

      <div>
        <div className="text-sm text-foreground">
          <Trans>Set up as a <span className="font-semibold text-primary">{i18n._(TYPE_LABEL_MSGS[chosenType] ?? TYPE_LABEL_MSGS.pa)}</span>.</Trans>{" "}
          <button
            onClick={() => { setShowRoles((o) => !o); }}
            className="text-xs text-foreground-muted hover:text-foreground underline underline-offset-2"
          >
            {showRoles ? <Trans>Hide other roles ▴</Trans> : <Trans>See other roles ▾</Trans>}
          </button>
        </div>
        {/* When the user keeps the suggestion, show the agent's reason for it
            (in their language). If they override, fall back to the static blurb
            for whatever type they picked. */}
        <div className="text-xs text-foreground-muted mt-1">
          {isSuggested && suggestion.type_reason ? suggestion.type_reason : i18n._(TYPE_BLURBS[chosenType])}
        </div>

        {showRoles && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            {TYPE_ORDER.map((slug) => (
              <button
                key={slug}
                onClick={() => { onChooseType(slug); setShowRoles(false); }}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition ${chosenType === slug ? "border-primary bg-primary-soft text-foreground" : "border-border bg-surface-elevated text-foreground hover:bg-surface-sunken"}`}
              >
                <div className="font-medium">{i18n._(TYPE_LABEL_MSGS[slug])}</div>
                <div className="text-[11px] text-foreground-muted mt-0.5">{i18n._(TYPE_BLURBS[slug])}</div>
                {slug === suggestion.agent_type && <div className="text-[10px] text-primary mt-0.5"><Trans>suggested</Trans></div>}
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        onClick={onConfirm}
        disabled={nameSubmitBlocked({ botName, nameError, checkingName, busy: hiring })}
        className="w-full px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary-hover transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {hiring ? <Trans>Setting up…</Trans> : <Trans>Confirm &amp; start</Trans>}
      </button>
    </div>
  );
}
